
import { supabase } from '@/lib/supabase';

export class EmailConfirmationService {
  /**
   * Detect if email confirmation is required based on Supabase settings
   */
  static async isEmailConfirmationRequired(): Promise<boolean> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        return !session.user.email_confirmed_at;
      }
      
      return true; // Default to requiring confirmation for safety
    } catch (error) {
      console.error('Error checking email confirmation requirement:', error);
      return true;
    }
  }

  /**
   * Save referral data to database for pending processing
   */
  static async savePendingReferralData(
    userId: string, 
    referralCode: string, 
    affiliateId: string
  ): Promise<boolean> {
    try {
      console.log('💾 Saving pending referral data for user:', userId);
      
      const { error } = await supabase
        .from('user_profiles')
        .update({
          pending_referral_code: referralCode,
          pending_affiliate_id: affiliateId,
          referral_processed: false,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId);

      if (error) {
        console.error('❌ Error saving pending referral data:', error);
        return false;
      }

      console.log('✅ Pending referral data saved successfully');
      return true;
    } catch (error) {
      console.error('❌ Exception saving pending referral data:', error);
      return false;
    }
  }

  /**
   * Get pending referral data for a user
   */
  static async getPendingReferralData(userId: string): Promise<{
    referralCode: string | null;
    affiliateId: string | null;
    needsProcessing: boolean;
  }> {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('pending_referral_code, pending_affiliate_id, referral_processed')
        .eq('user_id', userId)
        .single();

      if (error || !data) {
        return {
          referralCode: null,
          affiliateId: null,
          needsProcessing: false
        };
      }

      return {
        referralCode: data.pending_referral_code,
        affiliateId: data.pending_affiliate_id,
        needsProcessing: !data.referral_processed && !!data.pending_referral_code
      };
    } catch (error) {
      console.error('❌ Error getting pending referral data:', error);
      return {
        referralCode: null,
        affiliateId: null,
        needsProcessing: false
      };
    }
  }

  /**
   * Mark referral as processed
   */
  static async markReferralAsProcessed(userId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          referral_processed: true,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId);

      return !error;
    } catch (error) {
      console.error('❌ Error marking referral as processed:', error);
      return false;
    }
  }
}
